/**
 * sectionHelpers.ts
 *
 * Field-name fallback utilities for React site-builder renderers.
 *
 * Mirrors Python's _getf() from site_components/renderer.py so that
 * the editor canvas reads section data with the same priority as the
 * marketplace preview. Templates may use any of several synonymous
 * field names; these helpers pick the first non-empty value.
 */

/** Return the first non-empty string value from a set of candidate keys. */
export function getf(controls: Record<string, any>, ...keys: string[]): string {
  for (const key of keys) {
    const v = controls[key];
    if (v !== undefined && v !== null && v !== '') return String(v);
  }
  return '';
}

/** Return the first non-empty value of any type (for booleans, arrays, objects). */
export function getfRaw<T = any>(controls: Record<string, any>, ...keys: string[]): T | undefined {
  for (const key of keys) {
    const v = controls[key];
    if (v !== undefined && v !== null && v !== '') return v as T;
  }
  return undefined;
}

// ─── Common field groups (match Python _getf chains in renderer.py) ──────────

/** Section headline — tries headline → heading → section_title → title */
export const sfHeadline = (c: Record<string, any>, fallback = '') =>
  getf(c, 'headline', 'heading', 'section_title', 'title') || fallback;

/** Section subheadline — tries subheadline → subheading → subtitle → description */
export const sfSub = (c: Record<string, any>, fallback = '') =>
  getf(c, 'subheadline', 'subheading', 'subtitle', 'description') || fallback;

/** Primary CTA label */
export const sfCtaText = (c: Record<string, any>, fallback = 'Get Started') =>
  getf(c, 'cta_text', 'cta_label', 'cta_primary_text', 'cta_primary_label') || fallback;

/** Primary CTA URL */
export const sfCtaUrl = (c: Record<string, any>, fallback = '#') =>
  getf(c, 'cta_url', 'cta_href', 'cta_primary_url', 'cta_primary_href') || fallback;

/** Secondary CTA label */
export const sfCta2Text = (c: Record<string, any>, fallback = '') =>
  getf(c, 'cta_secondary_text', 'cta_secondary_label', 'cta2_text', 'cta2_label') || fallback;

/** Secondary CTA URL */
export const sfCta2Url = (c: Record<string, any>, fallback = '#') =>
  getf(c, 'cta_secondary_url', 'cta_secondary_href', 'cta2_url', 'cta2_href') || fallback;

/** Hero / section image URL */
export const sfImage = (c: Record<string, any>, fallback = '') =>
  getf(c, 'image_url', 'image', 'hero_image', 'background_image') || fallback;

/** Background image URL */
export const sfBgImage = (c: Record<string, any>, fallback = '') =>
  getf(c, 'background_image', 'image_url', 'image') || fallback;

/** Logo text / brand name */
export const sfLogoText = (c: Record<string, any>, fallback = 'Logo') =>
  getf(c, 'logo_text', 'brand_name', 'logo_alt') || fallback;

/** Author/testimonial name */
export const sfAuthorName = (c: Record<string, any>, fallback = '') =>
  getf(c, 'author_name', 'author', 'name') || fallback;

/** Author role / position */
export const sfAuthorRole = (c: Record<string, any>, fallback = '') =>
  getf(c, 'author_role', 'role', 'position', 'title') || fallback;

/** Author avatar URL */
export const sfAuthorImage = (c: Record<string, any>, fallback = '') =>
  getf(c, 'author_image', 'avatar', 'avatar_url', 'image_url') || fallback;

/** Body / description text */
export const sfBody = (c: Record<string, any>, fallback = '') =>
  getf(c, 'body_text', 'body', 'content', 'description', 'text') || fallback;

/** Submit button label */
export const sfSubmitText = (c: Record<string, any>, fallback = 'Submit') =>
  getf(c, 'submit_text', 'submit_label', 'button_text', 'cta_text') || fallback;

/** Badge / label chip text */
export const sfBadge = (c: Record<string, any>, fallback = '') =>
  getf(c, 'badge_text', 'badge', 'label', 'tag') || fallback;

/** Star rating (1-5) */
export const sfRating = (c: Record<string, any>, fallback = 5): number => {
  const v = getfRaw<number>(c, 'rating', 'star_rating', 'stars');
  return typeof v === 'number' ? v : fallback;
};
